/**
 * iter1038 — BHI Onboarding Mini Progress.
 *
 * Small always-visible progress chip rendered in the hero. Reads the same
 * `/api/breast-intelligence/onboarding-progress` payload the Getting Started
 * modal uses. Clicking it opens the Getting Started guide.
 *
 * Gap-fix: previously the 10-step progress bar was hidden inside the
 * Getting Started modal — patients forgot how far along they were.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { MapPin, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BhiOnboardingMiniProgress({ onOpen, isDark = false }) {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/onboarding-progress`);
        if (alive) setProgress(data);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [user]);

  if (!user || !progress) return null;

  const done = progress.done || 0;
  const total = progress.total || 10;
  const pct = progress.percent || Math.round((done / Math.max(total, 1)) * 100);
  const complete = done >= total;
  const critical = progress.critical_next_step;

  const wrapperTone = complete
    ? (isDark ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-emerald-300 bg-emerald-50 text-emerald-700")
    : (isDark ? "border-pink-500/40 bg-pink-500/10 text-pink-200"
              : "border-pink-300 bg-pink-50 text-pink-700");

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="bhi-onboarding-mini-progress"
      className={`w-full sm:w-auto inline-flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition hover:brightness-105 ${wrapperTone}`}
      title='Отвори „Откъде да започна"'
    >
      {complete ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <MapPin className="w-4 h-4 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold uppercase tracking-wider">
            Онбординг {done}/{total}
          </span>
          <span className="text-[11px] font-semibold opacity-70">· {pct}%</span>
        </div>
        <div className="mt-1 h-1 w-full sm:w-40 overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
          <div
            className={`h-full rounded-full transition-all ${
              complete ? "bg-emerald-500" : "bg-gradient-to-r from-pink-500 to-purple-500"
            }`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        {critical?.title && !complete && (
          <div className="mt-1 text-[10px] opacity-80 truncate">
            Следваща стъпка: {critical.title}
          </div>
        )}
      </div>
    </button>
  );
}
